import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDateIT, fmtEUR } from "@/lib/format";
import { Loader2 } from "lucide-react";
import { useFromState } from "@/lib/navContext";
import { usePersistedFilters } from "@/hooks/usePersistedFilters";
import { useScrollRestore } from "@/hooks/useScrollRestore";

const statoBadge = (s) => {
  const u = (s || "").toUpperCase();
  if (u === "LIQUIDATA") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (u === "RIFIUTATA") return "bg-rose-50 text-rose-700 border-rose-200";
  if (u === "RITIRATA") return "bg-slate-100 text-slate-600 border-slate-200";
  return "bg-sky-50 text-sky-700 border-sky-200";
};

export default function PracticesTab() {
  const [d, setD] = useState(null);
  const fromState = useFromState();
  const [f, setF] = usePersistedFilters("pa-pratiche", { dal: "", al: "", stato: "" });
  const { dal, al, stato } = f;
  const setDal = (v) => setF((s) => ({ ...s, dal: v }));
  const setAl = (v) => setF((s) => ({ ...s, al: v }));
  const setStato = (v) => setF((s) => ({ ...s, stato: v }));

  const load = useCallback(async () => {
    const r = await api.get("/portfolio-active/practices", { params: { dal: dal || undefined, al: al || undefined, stato: stato || undefined } });
    setD(r.data);
  }, [dal, al, stato]);
  useEffect(() => { load().catch(() => setD({ items: [] })); }, [load]);
  useScrollRestore(!!d);

  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const byMonth = {};
  (d.items || []).forEach((p) => { (byMonth[p.monthKey] = byMonth[p.monthKey] || []).push(p); });
  const months = Object.keys(byMonth).sort().reverse();

  return (
    <div className="space-y-4" data-testid="pa-practices">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-600">Periodo:</span>
        <input type="month" value={dal} onChange={(e) => setDal(e.target.value)} className="border border-slate-300 rounded-[3px] px-2 py-1" />
        <span className="text-slate-400">→</span>
        <input type="month" value={al} onChange={(e) => setAl(e.target.value)} className="border border-slate-300 rounded-[3px] px-2 py-1" />
        <select value={stato} onChange={(e) => setStato(e.target.value)} className="border border-slate-300 rounded-[3px] px-2 py-1.5 bg-white" data-testid="pa-practices-stato">
          <option value="">Stato: tutti</option>
          {["LIQUIDATA", "RIFIUTATA", "RITIRATA", "IN SVOLGIMENTO", "ATTESA CONTEGGI", "ATTESA DELIBERA"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <div className="ml-auto text-xs text-slate-600">
          <b>{fmtEUR(d.somma_liquidazioni_documentate)}</b> — {d.etichetta}
          {d.importi_liquidazione_mancanti > 0 && <span className="text-amber-700"> · {d.importi_liquidazione_mancanti} liquidate senza importo (escluse, non valgono zero)</span>}
        </div>
      </div>

      {months.map((mk) => (
        <div key={mk} className="bg-white border border-slate-200 rounded-[3px] overflow-x-auto">
          <div className="px-3 py-2 text-xs font-semibold text-slate-700 bg-slate-50 border-b border-slate-200">{mk} · {byMonth[mk].length} pratiche</div>
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                {["Cliente", "Prodotto", "Stato", "Finanziaria", "Caricata il", "Data esito", "Liquidata il", "Montante"].map((h) => <th key={h} className="py-1.5 px-3">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {byMonth[mk].map((p) => (
                <tr key={p.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50" data-testid={`pa-practice-${p.id}`}>
                  <td className="py-1.5 px-3"><Link to={`/clienti/${p.client_id}`} state={fromState} className="hover:text-[#B7955B]">{p.cliente}</Link></td>
                  <td className="py-1.5 px-3">{p.prodotto}</td>
                  <td className="py-1.5 px-3"><Link to={`/pipeline?mk=${p.monthKey}&focus=${p.id}`} state={fromState} className={`text-[10px] px-1.5 py-0.5 rounded-[3px] border ${statoBadge(p.stato)}`}>{p.stato}</Link></td>
                  <td className="py-1.5 px-3 text-slate-600">{p.finanziaria || <span className="text-slate-400 text-xs">da indicare</span>}</td>
                  <td className="py-1.5 px-3 tabular-nums">{p.caricata_il ? fmtDateIT(p.caricata_il) : "—"}</td>
                  <td className="py-1.5 px-3 tabular-nums">{p.esito_il ? fmtDateIT(p.esito_il) : <span className="text-slate-400">—</span>}</td>
                  <td className="py-1.5 px-3 tabular-nums">{p.liquidata_il ? fmtDateIT(p.liquidata_il) : "—"}</td>
                  <td className="py-1.5 px-3 tabular-nums">{p.montante > 0 ? fmtEUR(p.montante) : <span className="text-slate-400 text-xs">non documentato</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {months.length === 0 && <div className="py-8 text-center text-slate-400 text-sm bg-white border border-slate-200 rounded-[3px]">Nessuna pratica nel periodo scelto</div>}
    </div>
  );
}
